import dayjs from 'dayjs';
import { format } from 'date-fns';

/**
 * Converts a client created date string to a Date object for use in date range forms
 * @param clientCreatedDate - The client created date string
 * @returns Date object with UTC hours set to 0, or undefined if no date provided
 */
export const getClientCreatedDateForDateRange = (clientCreatedDate?: string): Date | undefined => {
  if (!clientCreatedDate) return undefined;

  const d = new Date(clientCreatedDate);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

/**
 * Converts a date value to a string while preserving date-only values without timezone shifting
 * @param dateValue - The date to convert (string, Date, or null/undefined)
 * @param fallbackToToday - Whether to fallback to today's date if no value provided (default: false)
 * @returns Date string in YYYY-MM-DD format for date-only values, ISO string for datetimes, or undefined
 */
export const convertDateToString = (
  dateValue?: string | Date | null,
  fallbackToToday: boolean = false
): string | undefined => {
  if (!dateValue) {
    if (fallbackToToday) {
      // Use dayjs to create today's date in YYYY-MM-DD format (local timezone)
      return dayjs().format('YYYY-MM-DD');
    }
    return undefined;
  }

  if (typeof dateValue === 'string') {
    // Check if it's a date-only string (YYYY-MM-DD format)
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyRegex.test(dateValue)) {
      // Ensure date-only strings are normalized as local dates
      return dayjs(dateValue, 'YYYY-MM-DD').format('YYYY-MM-DD');
    }
    // For other string formats, pass through unchanged (likely ISO datetime strings)
    return dateValue;
  }

  // Handle Date objects - check if it's a date-only value (midnight UTC)
  const utcHours = dateValue.getUTCHours();
  const utcMinutes = dateValue.getUTCMinutes();
  const utcSeconds = dateValue.getUTCSeconds();

  if (utcHours === 0 && utcMinutes === 0 && utcSeconds === 0 && dateValue.getUTCMilliseconds() === 0) {
    // This is likely a date-only value, extract UTC components and format as YYYY-MM-DD
    const year = dateValue.getUTCFullYear();
    const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() is 0-indexed
    const day = String(dateValue.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    // This is a proper datetime, keep ISO format
    return dateValue.toISOString();
  }
};

/**
 * Formats a date value for display, handling both strings and Date objects
 * while preserving date-only values without timezone shifting
 * @param dateValue - The date to format (string, Date, or null/undefined)
 * @returns Formatted date string (e.g., "Aug 13, 2024") or "Unknown" if invalid
 */
export const formatDate = (dateValue?: string | Date | null): string => {
  if (!dateValue) return 'Unknown';

  if (typeof dateValue === 'string') {
    // Check if it's a date-only string (YYYY-MM-DD format)
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyRegex.test(dateValue)) {
      // Use dayjs to parse as local date to avoid UTC timezone issues
      const localDate = dayjs(dateValue, 'YYYY-MM-DD').toDate();
      return format(localDate, 'MMM d, yyyy');
    } else {
      // For datetime strings, use dayjs to parse and then format with date-fns
      const dateObj = dayjs(dateValue).toDate();
      return format(dateObj, 'MMM d, yyyy');
    }
  } else {
    // Handle Date objects that might have been affected by UTC parsing
    // If the Date appears to be at midnight UTC, treat it as a date-only value
    const utcHours = dateValue.getUTCHours();
    const utcMinutes = dateValue.getUTCMinutes();
    const utcSeconds = dateValue.getUTCSeconds();

    if (utcHours === 0 && utcMinutes === 0 && utcSeconds === 0 && dateValue.getUTCMilliseconds() === 0) {
      // This is likely a date-only value that was parsed as UTC midnight
      // Extract UTC components and create a local date using dayjs to avoid timezone shifting
      const year = dateValue.getUTCFullYear();
      const month = dateValue.getUTCMonth();
      const day = dateValue.getUTCDate();
      const localDate = dayjs().year(year).month(month).date(day)
.toDate();
      return format(localDate, 'MMM d, yyyy');
    } else {
      // This is a proper datetime, format as-is
      return format(dateValue, 'MMM d, yyyy');
    }
  }
};
